import { getToken } from './utils'
import { notifySessionExpired, SESSION_EXPIRED_HEADER } from './sessionExpiry'
import { applyRefreshedToken, readRefreshedToken } from './tokenRefresh'

/*
La marca viaja en un header y no en el body a propósito: acá decidimos antes de leer el
body, y el body de una Response se puede leer una sola vez. Headers.get es
case-insensitive. Requiere que la API exponga el header por CORS (index.ts).
*/
const isSessionExpired = (response: Response): boolean => response.headers.get(SESSION_EXPIRED_HEADER) !== null

export const customFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = getToken()

  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  }

  const updatedOptions: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // Include cookies for session-based authentication
  }

  const response = await fetch(url, updatedOptions)

  /*
  Renovacion deslizante: si al token le quedaba poco, la API devuelve uno nuevo en un
  header y lo adoptamos sin molestar al usuario. Se hace ANTES de mirar response.ok
  porque una respuesta de error tambien pasa por authMiddleware y tambien lo trae.
  Ver tokenRefresh.ts.
  */
  applyRefreshedToken(readRefreshedToken(response))

  // Check if the response is not OK and throw an error
  if (!response.ok) {
    // Un 401 marcado por la API significa sesión vencida: hay que cerrarla, no
    // reintentar. Un 401 sin marca es un error normal (un tercero, o "no existe X")
    // y no debe cerrar nada. Ver sessionExpiry.ts.
    if (response.status === 401) notifySessionExpired(url, isSessionExpired(response))

    let errorMessage = `Error ${response.status}: ${response.statusText}`
    try {
      const errorText = await response.text()

      if (errorText) {
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.error ?? errorData.message ?? errorMessage
        } catch {
          errorMessage = errorText
        }
      }
    } catch {
      // Ignore
    }
    throw new Error(errorMessage)
  }

  return response.json() as Promise<T>
}

export const customPost = async <T, TBody = unknown>(url: string, bodyObj: TBody): Promise<T> => {
  const token = getToken()

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  }

  const body = JSON.stringify({ ...bodyObj })

  const updatedOptions: RequestInit = {
    method: 'POST',
    headers,
    body,
    credentials: 'include', // Include cookies for session-based authentication
  }

  const response = await fetch(url, updatedOptions)

  /*
  Renovacion deslizante: si al token le quedaba poco, la API devuelve uno nuevo en un
  header y lo adoptamos sin molestar al usuario. Se hace ANTES de mirar response.ok
  porque una respuesta de error tambien pasa por authMiddleware y tambien lo trae.
  Ver tokenRefresh.ts.
  */
  applyRefreshedToken(readRefreshedToken(response))

  if (!response.ok) {
    // Un 401 marcado por la API significa sesión vencida: hay que cerrarla, no
    // reintentar. Un 401 sin marca es un error normal (un tercero, o "no existe X")
    // y no debe cerrar nada. Ver sessionExpiry.ts.
    if (response.status === 401) notifySessionExpired(url, isSessionExpired(response))

    let errorMessage = `Error ${response.status}: ${response.statusText}`
    try {
      const errorText = await response.text()

      if (errorText) {
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.error ?? errorData.message ?? errorMessage
        } catch {
          errorMessage = errorText
        }
      }
    } catch {
      // Ignore
    }
    throw new Error(errorMessage)
  }

  // Return `undefined` if T is `void` to support empty responses
  if (response.status === 204 || !response.body) {
    return undefined as T
  }

  const responseData = await response.json()

  return responseData as T
}
export const customDelete = async <TBody = unknown>(url: string, bodyObj: TBody): Promise<void> => {
  const token = getToken()

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  }

  const body = JSON.stringify({ ...bodyObj })

  const updatedOptions: RequestInit = {
    method: 'DELETE',
    headers,
    body,
    credentials: 'include', // Include cookies for session-based authentication
  }

  const response = await fetch(url, updatedOptions)

  /*
  Renovacion deslizante: si al token le quedaba poco, la API devuelve uno nuevo en un
  header y lo adoptamos sin molestar al usuario. Se hace ANTES de mirar response.ok
  porque una respuesta de error tambien pasa por authMiddleware y tambien lo trae.
  Ver tokenRefresh.ts.
  */
  applyRefreshedToken(readRefreshedToken(response))

  if (!response.ok) {
    // Un 401 marcado por la API significa sesión vencida: hay que cerrarla, no
    // reintentar. Un 401 sin marca es un error normal (un tercero, o "no existe X")
    // y no debe cerrar nada. Ver sessionExpiry.ts.
    if (response.status === 401) notifySessionExpired(url, isSessionExpired(response))

    let errorMessage = `Error ${response.status}: ${response.statusText}`
    try {
      const errorText = await response.text()

      if (errorText) {
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.error ?? errorData.message ?? errorMessage
        } catch {
          errorMessage = errorText
        }
      }
    } catch {
      // Ignore
    }
    throw new Error(errorMessage)
  }

  // Return `undefined` if T is `void` to support empty responses
  if (response.status === 204 || !response.body) {
    return undefined
  }

  const responseData = await response.json()

  return responseData
}
